import { IntersectionTrigger } from "@aura-frontend/web";
import { Button, Input, Spinner } from "@win-reality/react-ui";
import { Search } from "lucide-react";
import { useState } from "react";
import { useDebounce } from "react-use";

import { useAvailablePlayers } from "@/hooks/useAvailablePlayers";

import { playerPublicToLineUpPlayer } from "../../api/omnibusMappers";
import { LineUpPlayer } from "../../types/gameFull";
import { PlayerName, RowShell } from "./PlayerRows";

const SEARCH_DEBOUNCE_MS = 300;

const SearchMessage = ({ children }: { children: string }) => (
  <div className="px-3 py-2 text-sm text-tagging-muted">{children}</div>
);

type OrgPlayerSearchProps = {
  /** The roster's org ("ORG#…"), searched by visibility group */
  orgId: string;
  /** Players on the roster or already picked: left out of the results */
  excludedPlayerIds: Set<string>;
  onAdd: (player: LineUpPlayer) => void;
};

/** The roster org's players, filtered by name, loading more on scroll. */
export const OrgPlayerSearch = ({
  orgId,
  excludedPlayerIds,
  onAdd,
}: OrgPlayerSearchProps) => {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useDebounce(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS, [
    search,
  ]);

  const {
    playersData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
  } = useAvailablePlayers({
    organizationId: orgId,
    continueString: "",
    debouncedSearchValue: debouncedSearch,
    enabled: true,
  });

  const players = (playersData?.pages ?? [])
    .flatMap((page) => page.players)
    .map((player) => playerPublicToLineUpPlayer(player))
    .filter((player) => !excludedPlayerIds.has(player.playerId));

  const renderResults = () => {
    if (status === "pending") {
      return <SearchMessage>Loading players…</SearchMessage>;
    }
    if (status === "error") {
      return <SearchMessage>Couldn't load players</SearchMessage>;
    }
    if (players.length === 0 && !hasNextPage) {
      return <SearchMessage>No players found</SearchMessage>;
    }
    return (
      <>
        {players.map((player) => (
          <RowShell
            key={player.playerId}
            data-testid="lineup-player-search-result-item"
            data-player-id={player.playerId}
          >
            <PlayerName player={player} />
            <Button
              variant="ghost"
              size="xs"
              onClick={() => onAdd(player)}
              data-testid="lineup-player-search-add-button"
              data-player-id={player.playerId}
              className="shrink-0 px-2 text-sm text-tagging-accent hover:underline"
            >
              + Add
            </Button>
          </RowShell>
        ))}
        {hasNextPage && (
          <IntersectionTrigger
            onIntersect={() => !isFetchingNextPage && fetchNextPage()}
          >
            <div className="flex justify-center py-2">
              {isFetchingNextPage && <Spinner />}
            </div>
          </IntersectionTrigger>
        )}
      </>
    );
  };

  return (
    <div className="space-y-1.5">
      <label
        htmlFor="add-players-search"
        className="block text-sm font-semibold"
      >
        Search Organization Players
      </label>
      <div className="relative">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          id="add-players-search"
          data-testid="lineup-player-search-input"
          className="pr-10"
        />
        <Search className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-tagging-muted" />
      </div>
      <div className="tagging-scrollbar max-h-56 space-y-2 overflow-y-auto pt-1">
        {renderResults()}
      </div>
    </div>
  );
};
